import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  MenuItem,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { adminSystemApi } from '../services/api';
import { SystemInfo, BackupInfo } from '../types';

const AdminSystem: React.FC = () => {
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [systemLogs, setSystemLogs] = useState<string[]>([]);
  const [logType, setLogType] = useState<'error' | 'access' | 'system'>('error');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openBackupDialog, setOpenBackupDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<string>('');
  const [backupName, setBackupName] = useState('');

  useEffect(() => {
    fetchSystemInfo();
    fetchBackups();
    fetchSystemLogs();
  }, []);

  useEffect(() => {
    fetchSystemLogs();
  }, [logType]);

  const fetchSystemInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminSystemApi.getSystemInfo();
      if (response.status === 'success' && response.data) {
        // Convert SystemHealth to SystemInfo format
        const systemInfo: SystemInfo = {
          cpu_usage: response.data.cpu_usage,
          memory_usage: response.data.memory_usage,
          disk_usage: response.data.disk_usage,
          database_size: 'Unknown',
          last_backup_time: response.data.last_backup || 'Never',
        };
        setSystemInfo(systemInfo);
      } else {
        setError(response.message || 'Failed to fetch system information');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An unexpected error occurred');
      console.error('Fetch system info error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    try {
      const response = await adminSystemApi.getBackups();
      if (response.status === 'success' && response.data) {
        // Convert backup data format
        const backupList: BackupInfo[] = response.data.backups.map((backup: any) => ({
          filename: backup.path.split('/').pop() || backup.path,
          size: backup.size,
          created_at: backup.created_at,
        }));
        setBackups(backupList);
      }
    } catch (err: any) {
      console.error('Fetch backups error:', err);
    }
  };

  const fetchSystemLogs = async () => {
    try {
      const response = await adminSystemApi.getSystemLogs({ level: logType, limit: 50 });
      if (response.status === 'success' && response.data) {
        setSystemLogs(response.data.logs || []);
      }
    } catch (err: any) {
      console.error('Fetch system logs error:', err);
      setSystemLogs([]);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setError(null);
      setSuccess(null);
      await adminSystemApi.createBackup();
      setSuccess('Backup created successfully!');
      setOpenBackupDialog(false);
      setBackupName('');
      fetchBackups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create backup');
      console.error('Create backup error:', err);
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;
    
    try {
      setError(null);
      setSuccess(null);
      await adminSystemApi.restoreBackup(selectedBackup);
      setSuccess('Backup restored successfully!');
      setOpenRestoreDialog(false);
      setSelectedBackup('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to restore backup');
      console.error('Restore backup error:', err);
    }
  };

  const handleDeleteBackup = async (filename: string) => {
    if (window.confirm(`Are you sure you want to delete backup ${filename}?`)) {
      try {
        setError(null);
        setSuccess(null);
        // Mock delete backup - API method doesn't exist yet
      console.log('Delete backup:', filename);
        setSuccess('Backup deleted successfully!');
        fetchBackups();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete backup');
        console.error('Delete backup error:', err);
      }
    }
  };

  const getStatusColor = (value: number, thresholds: { warning: number; critical: number }) => {
    if (value >= thresholds.critical) return 'error';
    if (value >= thresholds.warning) return 'warning';
    return 'success';
  };

  const getStatusIcon = (value: number, thresholds: { warning: number; critical: number }) => {
    if (value >= thresholds.critical) return <ErrorIcon />;
    if (value >= thresholds.warning) return <WarningIcon />;
    return <CheckCircleIcon />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
        gap: 3 
      }}>
        {/* System Health */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MemoryIcon />
              System Health
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {systemInfo ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* CPU Usage */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2">CPU Usage</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color={`${getStatusColor(systemInfo.cpu_usage, { warning: 70, critical: 90 })}.main`}>
                        {systemInfo.cpu_usage}%
                      </Typography>
                      {getStatusIcon(systemInfo.cpu_usage, { warning: 70, critical: 90 })}
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={systemInfo.cpu_usage}
                    color={getStatusColor(systemInfo.cpu_usage, { warning: 70, critical: 90 })}
                  />
                </Box>

                {/* Memory Usage */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2">Memory Usage</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color={`${getStatusColor(systemInfo.memory_usage, { warning: 80, critical: 95 })}.main`}>
                        {systemInfo.memory_usage}%
                      </Typography>
                      {getStatusIcon(systemInfo.memory_usage, { warning: 80, critical: 95 })}
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={systemInfo.memory_usage}
                    color={getStatusColor(systemInfo.memory_usage, { warning: 80, critical: 95 })}
                  />
                </Box>

                {/* Disk Usage */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2">Disk Usage</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color={`${getStatusColor(systemInfo.disk_usage, { warning: 85, critical: 95 })}.main`}>
                        {systemInfo.disk_usage}%
                      </Typography>
                      {getStatusIcon(systemInfo.disk_usage, { warning: 85, critical: 95 })}
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={systemInfo.disk_usage}
                    color={getStatusColor(systemInfo.disk_usage, { warning: 85, critical: 95 })}
                  />
                </Box>

                {/* Database Size */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Database Size</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemInfo.database_size}
                  </Typography>
                </Box>

                {/* Last Backup */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Last Backup</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemInfo.last_backup_time || 'Never'}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                System information not available
              </Typography>
            )}

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchSystemInfo}
              sx={{ mt: 3 }}
              fullWidth
            >
              Refresh System Info
            </Button>
          </CardContent>
        </Card>

        {/* Backup Management */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorageIcon />
              Backup Management
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => setOpenBackupDialog(true)}
                fullWidth
              >
                Create Backup
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => setOpenRestoreDialog(true)}
                fullWidth
                disabled={backups.length === 0}
              >
                Restore Backup
              </Button>
            </Box>

            {backups.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Filename</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backups.map((backup, index) => (
                      <TableRow key={index}>
                        <TableCell>{backup.filename}</TableCell>
                        <TableCell>{backup.size}</TableCell>
                        <TableCell>{backup.created_at}</TableCell>
                        <TableCell>
                          <Tooltip title="Delete Backup">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteBackup(backup.filename)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                No backups available
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* System Logs */}
      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon />
                System Logs
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="Log Type"
                  value={logType}
                  onChange={(e) => setLogType(e.target.value as 'error' | 'access' | 'system')}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="error">Error Logs</MenuItem>
                  <MenuItem value="access">Access Logs</MenuItem>
                  <MenuItem value="system">System Logs</MenuItem>
                </TextField>
                <IconButton onClick={fetchSystemLogs} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ 
              backgroundColor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              maxHeight: 300,
              overflow: 'auto'
            }}>
              {systemLogs.length > 0 ? (
                systemLogs.map((log, index) => (
                  <Typography key={index} variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                    {log}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No {logType} logs available or unable to read log files.
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Create Backup Dialog */}
      <Dialog open={openBackupDialog} onClose={() => setOpenBackupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create System Backup</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Backup Name (Optional)"
            value={backupName}
            onChange={(e) => setBackupName(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Leave empty for automatic naming"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBackupDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateBackup} variant="contained">
            Create Backup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Backup Dialog */}
      <Dialog open={openRestoreDialog} onClose={() => setOpenRestoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Restore System Backup</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Select Backup"
            value={selectedBackup}
            onChange={(e) => setSelectedBackup(e.target.value)}
            sx={{ mt: 2 }}
          >
            {backups.map((backup, index) => (
              <MenuItem key={index} value={backup.filename}>
                {backup.filename} ({backup.size}) - {backup.created_at}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRestoreDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleRestoreBackup} 
            variant="contained"
            disabled={!selectedBackup}
          >
            Restore Backup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSystem;